import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Circle, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import AppHeader from "@/components/AppHeader";
import BackButton from "@/components/BackButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, Camera, MapPin, Loader2, X, Clock, Locate, Info } from "lucide-react";

// red urgent pin
const urgentIcon = new L.DivIcon({
  className: "",
  html: '<div style="width:26px;height:26px;background:#EF4444;border:3px solid #fff;border-radius:50%;box-shadow:0 4px 10px rgba(0,0,0,.35)"></div>',
  iconSize: [26, 26], iconAnchor: [13, 13],
});

function ClickToSelect({ onSelect }) {
  useMapEvents({ click: (e) => onSelect(e.latlng) });
  return null;
}
function CenterOn({ pos }) {
  const map = useMap();
  useEffect(() => { if (pos) map.flyTo([pos.lat, pos.lng], Math.max(map.getZoom(), 15), { duration: 0.6 }); }, [pos, map]);
  return null;
}

const CATS = {
  animals: ["suspicious_baits", "hunting_activity", "wildlife_distress", "other_urgent"],
  environment: ["smoke_emissions", "fire", "hazardous_substance", "water_leak", "flood", "other_urgent"],
  urban: ["fallen_tree", "road_obstacle", "dangerous_branches", "other_urgent"],
  waste: ["hazardous", "explosive_flammable", "chemicals", "medical_waste", "other_urgent"],
};

export default function UrgentAlertCreate() {
  const { user, loading } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [category, setCategory] = useState("environment");
  const [subcategory, setSubcategory] = useState("");
  const [otherSub, setOtherSub] = useState("");
  const [description, setDescription] = useState("");
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [radius, setRadius] = useState(300);
  const [duration, setDuration] = useState(3);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [geoDenied, setGeoDenied] = useState(false);
  const mapPos = useMemo(() => {
    const la = parseFloat(lat); const ln = parseFloat(lng);
    return isFinite(la) && isFinite(ln) ? { lat: la, lng: ln } : null;
  }, [lat, lng]);

  const centerOnMe = () => {
    if (!navigator.geolocation) { setGeoDenied(true); toast.error(t("gps_unavailable")); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => { setLat(p.coords.latitude.toFixed(6)); setLng(p.coords.longitude.toFixed(6)); setGeoDenied(false); toast.success(t("gps_updated")); },
      (err) => { if (err && err.code === 1) setGeoDenied(true); toast.error(t("gps_pick_manual")); }
    );
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/auth"); return; }
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => { setLat(p.coords.latitude.toFixed(6)); setLng(p.coords.longitude.toFixed(6)); },
        () => {}
      );
    }
  }, [user, loading, nav]);

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos((p) => [...p, { path: data.path, url: `${process.env.REACT_APP_BACKEND_URL}${data.url}` }]);
    } catch { toast.error(t("photo_upload_failed")); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!lat || !lng) { toast.error(t("gps_missing")); return; }
    if (!description || description.trim().length < 8) { toast.error(t("description_too_short")); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/urgent-alerts", {
        category, subcategory: subcategory || "other_urgent",
        other_subcategory: otherSub || undefined,
        description,
        lat: parseFloat(lat), lng: parseFloat(lng),
        radius_m: parseInt(radius, 10),
        duration_hours: parseInt(duration, 10),
        photos: photos.map((p) => p.path),
      });
      toast.success(t("urgent_created"));
      nav(`/urgent/${data.alert_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || t("generic_error")); }
    finally { setSubmitting(false); }
  };

  const subs = CATS[category] || ["other_urgent"];

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="urgent-create-page">
      <AppHeader />
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8">
        <BackButton className="mb-4" testid="urgent-back-btn" />
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.25em] text-red-700 font-semibold flex items-center gap-2"><AlertTriangle size={12} /> {t("urgent_alert")}</div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mt-2">{t("urgent_new")}</h1>
          <p className="mt-2 text-slate-600">{t("urgent_desc")}</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4">
          {/* Short explanation banner */}
          <div className="rounded-xl border border-red-200 bg-red-50/60 p-3 flex items-start gap-2.5" data-testid="urgent-short-explanation">
            <Info size={16} className="text-red-700 mt-0.5 shrink-0" />
            <div className="text-xs text-red-900 leading-relaxed">{t("urgent_short_explanation")}</div>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("category")}</Label>
              <Select value={category} onValueChange={(v) => { setCategory(v); setSubcategory(""); }}>
                <SelectTrigger data-testid="urgent-category" className="mt-1.5 h-11 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="environment">{t("cat_environment")}</SelectItem>
                  <SelectItem value="animals">{t("cat_animals")}</SelectItem>
                  <SelectItem value="urban">{t("cat_urban")}</SelectItem>
                  <SelectItem value="waste">{t("cat_waste")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("subcategory")}</Label>
              <Select value={subcategory} onValueChange={setSubcategory}>
                <SelectTrigger data-testid="urgent-subcategory" className="mt-1.5 h-11 rounded-xl"><SelectValue placeholder={t("select_subcategory")} /></SelectTrigger>
                <SelectContent>
                  {subs.map((s) => <SelectItem key={s} value={s}>{s === "other_urgent" ? t("urgent_other") : (t(`sub_${s}`) || s.replace(/_/g," "))}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {subcategory === "other_urgent" && (
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("urgent_other")}</Label>
              <Input data-testid="urgent-other-sub" value={otherSub} onChange={(e) => setOtherSub(e.target.value)} className="mt-1.5 h-11 rounded-xl" placeholder="es. animale ferito, sostanza sconosciuta…" />
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("description")}</Label>
            <Textarea data-testid="urgent-description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t("urgent_free_desc")} className="mt-1.5 min-h-[120px] rounded-xl" />
          </div>

          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("latitude")}</Label>
              <Input data-testid="urgent-lat" value={lat} onChange={(e) => setLat(e.target.value)} className="mt-1.5 h-11 rounded-xl" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("longitude")}</Label>
              <Input data-testid="urgent-lng" value={lng} onChange={(e) => setLng(e.target.value)} className="mt-1.5 h-11 rounded-xl" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("urgent_radius")}</Label>
              <Input data-testid="urgent-radius" type="number" min="10" max="5000" value={radius} onChange={(e) => setRadius(e.target.value)} className="mt-1.5 h-11 rounded-xl" />
            </div>
          </div>

          {/* Map picker */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5"><MapPin size={12} /> {t("urgent_map_pick")}</Label>
              <Button data-testid="urgent-center-me" type="button" size="sm" variant="outline" onClick={centerOnMe} className="rounded-full h-8 text-xs hover:bg-emerald-50 hover:text-emerald-800"><Locate size={12} className="mr-1.5" /> {t("urgent_center_me")}</Button>
            </div>
            {geoDenied && (
              <div className="mb-2 p-2.5 rounded-lg bg-yellow-50 border border-yellow-200 text-xs text-yellow-900 flex items-start gap-2" data-testid="urgent-geo-denied">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <span>{t("geo_denied_msg")}</span>
              </div>
            )}
            <div className="h-72 rounded-2xl overflow-hidden border border-slate-200" data-testid="urgent-map">
              <MapContainer center={mapPos ? [mapPos.lat, mapPos.lng] : [45.4642, 9.19]} zoom={mapPos ? 15 : 5} className="w-full h-full" scrollWheelZoom={true}>
                <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap" />
                {mapPos && <Marker position={[mapPos.lat, mapPos.lng]} icon={urgentIcon} />}
                {mapPos && <Circle center={[mapPos.lat, mapPos.lng]} radius={parseInt(radius, 10) || 200} pathOptions={{ color: "#EF4444", fillColor: "#EF4444", fillOpacity: 0.15 }} />}
                <ClickToSelect onSelect={(latlng) => { setLat(latlng.lat.toFixed(6)); setLng(latlng.lng.toFixed(6)); }} />
                <CenterOn pos={mapPos} />
              </MapContainer>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5"><Clock size={12} /> {t("urgent_duration")}</Label>
            <div className="mt-2 grid grid-cols-5 gap-2" data-testid="urgent-duration-group">
              {[1,3,6,12,24].map((h) => (
                <button
                  key={h}
                  type="button"
                  data-testid={`urgent-duration-${h}`}
                  onClick={() => setDuration(h)}
                  className={`h-11 rounded-xl font-semibold text-sm border-2 ${duration === h ? "bg-red-600 text-white border-red-600" : "bg-white text-slate-700 border-slate-200 hover:border-red-300"}`}
                >{t(`urgent_${h}h`)}</button>
              ))}
            </div>
          </div>

          {/* photos */}
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("upload_photos")}</Label>
            <div className="mt-2 grid grid-cols-3 md:grid-cols-4 gap-3">
              {photos.map((p, i) => (
                <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-slate-200 group" data-testid={`urgent-photo-${i}`}>
                  <img src={p.url} alt="upload" className="w-full h-full object-cover" />
                  <button onClick={() => setPhotos(photos.filter((_, idx) => idx !== i))} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100"><X size={12} /></button>
                </div>
              ))}
              <label className="aspect-square rounded-xl border-2 border-dashed border-emerald-300 flex flex-col items-center justify-center cursor-pointer hover:border-emerald-500 hover:bg-emerald-50 text-emerald-700" data-testid="urgent-take-photo">
                {uploading ? <Loader2 className="animate-spin" size={20} /> : <Camera size={20} />}
                <span className="text-[11px] mt-1 font-semibold">{t("take_photo")}</span>
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} />
              </label>
              <label className="aspect-square rounded-xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center cursor-pointer hover:border-emerald-500 hover:bg-emerald-50 text-slate-500 hover:text-emerald-700" data-testid="urgent-gallery">
                {uploading ? <Loader2 className="animate-spin" size={20} /> : <Camera size={20} />}
                <span className="text-[11px] mt-1">{t("from_gallery")}</span>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} />
              </label>
            </div>
          </div>

          <div className="pt-4 flex gap-3">
            <Button data-testid="urgent-submit" onClick={submit} disabled={submitting} size="lg" className="rounded-full bg-red-600 hover:bg-red-700 hover:text-white text-white flex-1">
              <AlertTriangle size={16} className="mr-2" /> {submitting ? "..." : t("urgent_submit")}
            </Button>
            <Button variant="ghost" onClick={() => nav(-1)} className="rounded-full hover:bg-slate-100 hover:text-slate-900">{t("cancel")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
